/**
 * Initialization - page setup and event handlers
 * Converted from testing.js
 */

import { $dom, ready } from '../utils/DOM';
import { gameController } from './GameController';
import { Settings } from './Settings';
const PlayerSettings = Settings as any;
import { Globals } from './Globals';
import { GameUI } from './GameUI';
import { runEventScripts, runGlobalScriptsOnce } from './ScriptRunner';
import { GameCommands } from '../game/GameCommands';
import { GameActions } from '../game/GameActions';
import { GameTimers } from '../game/GameTimers';
import { GameHistory } from '../game/GameHistory';
import { Finder } from '../utils/Finder';
import { ActionRecorder } from '../utils/Logging';
import { GetDestinationRoomName, roomDisplayName, ChangeRoomAsync, RoomChange, SetupStatusBars, AddTextToRTF, SetPortrait, showImage, imagePath, getObjectClass, UpdateStatusBars, ResetLoopObjects, maybeAppendTurnSeparatorForInput, mainTextEndsWithTurnSeparator, processTextForOutput, isLocalFileContext } from '../utils/Utilities';
import { SavedGames as SavedGamesModule, type SaveData } from '../game/SaveAndLoad';
import { loadGamePackage, abortDownload } from '@/core/Package';
import { initializeProgressIndicator } from './PackageProgressIndicator';
import { REGALIA_PLAYER_VERSION, compareSemver, isLegacyRegaliaVersion } from '../utils/Versioning';

declare let TheGame: any;
declare function setupGenderSelection(): void;

// Forward declarations
declare function StartGame(): void;
declare function SetupGameDataWithMap(): any;
declare var OriginalGame: any;
declare var SetupGameData: () => any;

let updatePortraitLayout = (): void => {};
let updateInventoryLayout = (): void => {};
let isGameCompatible = (_game: any): boolean => true;
let isSaveVersionMismatch = (_savedGame: any): boolean => false;

function hideSaveAndLoadMenus(): void {
    $dom(".save-menu").addClass("hidden");
}

ready(() => {
    if (!(window.File && window.FileReader && window.FileList && window.Blob)) {
        (window as any).showAlert?.("Browser Compatibility", 'The File APIs are not fully supported in this browser.');
    }

    $dom('#regalia_version').text(`Regalia ${REGALIA_PLAYER_VERSION}`);

    const localFileContext = isLocalFileContext();
    if (localFileContext) {
        PlayerSettings.enablePreloading = false;
    }

    // Initialize package progress indicator
    initializeProgressIndicator();

    // Abort downloads on beforeunload
    window.addEventListener('beforeunload', () => {
        abortDownload();
    });

    // Initialize Perfect Scrollbar on MainText
    // Wait a bit to ensure Perfect Scrollbar is loaded
    setTimeout(() => {
        {
            const mainTextEl = document.getElementById('MainText');
            if (mainTextEl && (window as any).PerfectScrollbar) {
                (window as any).mainTextScrollbar = new (window as any).PerfectScrollbar(mainTextEl, {
                    wheelSpeed: 1,
                    wheelPropagation: false,
                    minScrollbarLength: 20
                });

                // Update Perfect Scrollbar when MainText is resized
                const resizeObserver = new ResizeObserver(() => {
                    if ((window as any).mainTextScrollbar) {
                        (window as any).mainTextScrollbar.update();
                        // Ensure scroll position is valid after resize
                        const scrollTop = mainTextEl.scrollTop;
                        const maxScrollTop = mainTextEl.scrollHeight - mainTextEl.clientHeight;
                        if (scrollTop > maxScrollTop && maxScrollTop >= 0) {
                            mainTextEl.scrollTop = maxScrollTop;
                            (window as any).mainTextScrollbar.update();
                        }
                    }
                });
                resizeObserver.observe(mainTextEl);

                // Also listen for window resize as a fallback
                let resizeTimeout: number | null = null;
                window.addEventListener('resize', () => {
                    if (resizeTimeout !== null) {
                        clearTimeout(resizeTimeout);
                    }
                    resizeTimeout = window.setTimeout(() => {
                        if ((window as any).mainTextScrollbar) {
                            (window as any).mainTextScrollbar.update();
                            // Ensure scroll position is valid after resize
                            const scrollTop = mainTextEl.scrollTop;
                            const maxScrollTop = mainTextEl.scrollHeight - mainTextEl.clientHeight;
                            if (scrollTop > maxScrollTop && maxScrollTop >= 0) {
                                mainTextEl.scrollTop = maxScrollTop;
                                (window as any).mainTextScrollbar.update();
                            }
                        }
                    }, 100);
                });
            }
        }

        for (let i of ['RoomObjects', 'VisibleCharacters', 'Inventory'])
        {
            const el = document.getElementById(i);
            if (el && (window as any).PerfectScrollbar)
            {
                (window as any).roomObjectsScrollbar = new (window as any).PerfectScrollbar(el, {
                    wheelSpeed: 1,
                    wheelPropagation: false,
                    minScrollbarLength: 20
                });

                // Update Perfect Scrollbar when MainText is resized
                const resizeObserver = new ResizeObserver(() => {
                    if ((window as any).mainTextScrollbar) {
                        (window as any).mainTextScrollbar.update();
                        // Ensure scroll position is valid after resize
                        const scrollTop = el.scrollTop;
                        const maxScrollTop = el.scrollHeight - el.clientHeight;
                        if (scrollTop > maxScrollTop && maxScrollTop >= 0) {
                            el.scrollTop = maxScrollTop;
                            (window as any).mainTextScrollbar.update();
                        }
                    }
                });
                resizeObserver.observe(el);

                // Also listen for window resize as a fallback
                let resizeTimeout: number | null = null;
                window.addEventListener('resize', () => {
                    if (resizeTimeout !== null) {
                        clearTimeout(resizeTimeout);
                    }
                    resizeTimeout = window.setTimeout(() => {
                        if ((window as any).mainTextScrollbar) {
                            (window as any).mainTextScrollbar.update();
                            // Ensure scroll position is valid after resize
                            const scrollTop = el.scrollTop;
                            const maxScrollTop = el.scrollHeight - el.clientHeight;
                            if (scrollTop > maxScrollTop && maxScrollTop >= 0) {
                                el.scrollTop = maxScrollTop;
                                (window as any).mainTextScrollbar.update();
                            }
                        }
                    }, 100);
                });
            }
        }
    }, 100);

    // Track if splits are already initialized
    let splitsInitialized = false;
    const splitInstances: any[] = [];
    // Store split instances by name for easy access
    const splitInstancesMap: Map<string, any> = new Map();
    let roomVisibleSplitCount = 3;
    
    // Default split sizes (for double-click reset)
    const defaultSplitSizes = {
        splitRoomPicPanel: 40,
        splitMainPlayerRow: 70,
        splitCompassRoomObjects: 40,
        splitRoomObjectsVisibleChars: 33.33,
        splitVisibleCharsInventory: 33.33 // Equal widths for all three panels
    };
    const COMPASS_ONLY_FRACTION = 0.6;
    const DEFAULT_INVENTORY_SHARE = 33.33;

    function clampSplitSize(size: number): number {
        if (!Number.isFinite(size)) return 0;
        return Math.max(0, Math.min(100, size));
    }

    function isPortraitHidden(): boolean {
        return !!TheGame?.HidePortrait;
    }

    function isInventoryHidden(): boolean {
        return TheGame?.ShowInventory === false;
    }

    function getStoredInventoryShare(): number {
        const roomShare = clampSplitSize(PlayerSettings.splitRoomObjectsVisibleChars);
        const visibleShare = clampSplitSize(PlayerSettings.splitVisibleCharsInventory);
        const inventoryShare = 100 - roomShare - visibleShare;
        return clampSplitSize(inventoryShare);
    }

    function getEffectiveRoomVisibleSplitSizes(): [number, number, number] {
        const roomShare = clampSplitSize(PlayerSettings.splitRoomObjectsVisibleChars);
        const visibleShare = clampSplitSize(PlayerSettings.splitVisibleCharsInventory);
        const inventoryShare = getStoredInventoryShare();
        if (!isInventoryHidden()) {
            return [roomShare, visibleShare, clampSplitSize(100 - roomShare - visibleShare)];
        }
        const remaining = 100 - inventoryShare;
        if (remaining <= 0) {
            return [50, 50, 0];
        }
        const roomEffective = clampSplitSize((roomShare / remaining) * 100);
        const visibleEffective = clampSplitSize(100 - roomEffective);
        return [roomEffective, visibleEffective, 0];
    }

    function updateStoredRoomVisibleShares(roomEffective: number, visibleEffective: number): void {
        if (!isInventoryHidden()) {
            PlayerSettings.splitRoomObjectsVisibleChars = clampSplitSize(roomEffective);
            PlayerSettings.splitVisibleCharsInventory = clampSplitSize(visibleEffective);
            return;
        }
        const inventoryShare = getStoredInventoryShare();
        const remaining = 100 - inventoryShare;
        const safeRemaining = remaining > 0 ? remaining : (100 - DEFAULT_INVENTORY_SHARE);
        const roomShare = clampSplitSize((roomEffective / 100) * safeRemaining);
        const visibleShare = clampSplitSize(safeRemaining - roomShare);
        PlayerSettings.splitRoomObjectsVisibleChars = roomShare;
        PlayerSettings.splitVisibleCharsInventory = visibleShare;
    }

    function applyRoomVisibleSplitFromSettings(): void {
        const split = splitInstancesMap.get('splitRoomObjectsVisibleChars');
        if (split && typeof split.setSizes === 'function') {
            const [roomShare, visibleShare, inventoryShare] = getEffectiveRoomVisibleSplitSizes();
            if (roomVisibleSplitCount === 2) {
                split.setSizes([roomShare, visibleShare]);
            } else {
                split.setSizes([roomShare, visibleShare, inventoryShare]);
            }
        }
    }

    function getEffectiveCompassSplitSize(storedSize: number): number {
        const fraction = isPortraitHidden() ? COMPASS_ONLY_FRACTION : 1;
        return clampSplitSize(storedSize * fraction);
    }

    function getStoredCompassSplitSize(effectiveSize: number): number {
        if (!isPortraitHidden()) return clampSplitSize(effectiveSize);
        return clampSplitSize(effectiveSize / COMPASS_ONLY_FRACTION);
    }

    function applyCompassSplitSizeFromSettings(): void {
        const split = splitInstancesMap.get('splitCompassRoomObjects');
        if (split && typeof split.setSizes === 'function') {
            const effectiveSize = getEffectiveCompassSplitSize(PlayerSettings.splitCompassRoomObjects);
            split.setSizes([effectiveSize, 100 - effectiveSize]);
        }
    }

    // Initialize Split.js for resizable panels
    function initializeSplits(): void {
        if (!(window as any).Split) {
            console.warn('Split.js not loaded');
            return;
        }

        // Don't reinitialize if already done (unless explicitly requested)
        if (splitsInitialized) {
            return;
        }

        const Split = (window as any).Split;
        
        // Helper function to add double-click reset to a split
        function addDoubleClickReset(splitInstance: any, elements: HTMLElement[], defaultSizes: number[], settingName: string): void {
            // Find the gutters created by this split
            // Split.js creates gutters between elements, so we need to find them
            setTimeout(() => {
                const parent = elements[0].parentElement;
                if (!parent) return;
                
                // Find all gutters (they have class 'gutter')
                const gutters = parent.querySelectorAll('.gutter');
                gutters.forEach((gutter: Element) => {
                    // Check if this gutter is between our elements by checking if it's a sibling
                    let isBetween = false;
                    // Check if gutter is between any of our elements
                    for (let i = 0; i < elements.length - 1; i++) {
                        const el1 = elements[i];
                        const el2 = elements[i + 1];
                        // Gutter should be between el1 and el2
                        if ((gutter.nextElementSibling === el2 && gutter.previousElementSibling === el1) ||
                            (gutter.previousElementSibling === el2 && gutter.nextElementSibling === el1) ||
                            (el1.nextElementSibling === gutter && gutter.nextElementSibling === el2) ||
                            (el2.nextElementSibling === gutter && gutter.nextElementSibling === el1)) {
                            isBetween = true;
                            break;
                        }
                    }
                    
                    if (isBetween) {
                        // Add double-click listener directly to the existing gutter
                        // Don't replace the gutter as that would break Split.js drag handlers
                        (gutter as HTMLElement).addEventListener('dblclick', () => {
                            // Reset to default sizes using setSizes method
                            if (splitInstance && typeof splitInstance.setSizes === 'function') {
                                let sizesToApply = defaultSizes;
                                if (settingName === 'splitCompassRoomObjects') {
                                    const effectiveSize = getEffectiveCompassSplitSize(defaultSplitSizes.splitCompassRoomObjects);
                                    sizesToApply = [effectiveSize, 100 - effectiveSize];
                                }
                                splitInstance.setSizes(sizesToApply);
                            }
                            // Update setting
                            if (settingName === 'splitRoomPicPanel') {
                                PlayerSettings.splitRoomPicPanel = defaultSizes[0];
                            } else if (settingName === 'splitMainPlayerRow') {
                                PlayerSettings.splitMainPlayerRow = defaultSizes[0];
                            } else if (settingName === 'splitCompassRoomObjects') {
                                const effectiveSize = getEffectiveCompassSplitSize(defaultSplitSizes.splitCompassRoomObjects);
                                PlayerSettings.splitCompassRoomObjects = getStoredCompassSplitSize(effectiveSize);
                            } else if (settingName === 'splitRoomObjectsVisibleChars') {
                                updateStoredRoomVisibleShares(defaultSizes[0], defaultSizes[1]);
                            }
                            // Update Perfect Scrollbar after reset
                            const mainTextEl = document.getElementById('MainText');
                            if (mainTextEl && (window as any).mainTextScrollbar) {
                                (window as any).mainTextScrollbar.update();
                                // Ensure scroll position is valid
                                const scrollTop = mainTextEl.scrollTop;
                                const maxScrollTop = mainTextEl.scrollHeight - mainTextEl.clientHeight;
                                if (scrollTop > maxScrollTop && maxScrollTop >= 0) {
                                    mainTextEl.scrollTop = maxScrollTop;
                                    (window as any).mainTextScrollbar.update();
                                }
                            }
                        });
                    }
                });
            }, 100);
        }

        // 1. Split between roompanel and picpanel (horizontal)
        const roomPanel = document.getElementById('roompanel');
        const picPanel = document.querySelector('.picpanel') as HTMLElement;
        if (roomPanel && picPanel) {
            const savedSize = PlayerSettings.splitRoomPicPanel;
            const split1 = Split([roomPanel, picPanel], {
                sizes: [savedSize, 100 - savedSize],
                minSize: [200, 200],
                gutterSize: 5,
                cursor: 'col-resize',
                onDragEnd: (sizes: number[]) => {
                    PlayerSettings.splitRoomPicPanel = sizes[0];
                    // Update Perfect Scrollbar after resize
                    const mainTextEl = document.getElementById('MainText');
                    if (mainTextEl && (window as any).mainTextScrollbar) {
                        (window as any).mainTextScrollbar.update();
                        // Ensure scroll position is valid
                        const scrollTop = mainTextEl.scrollTop;
                        const maxScrollTop = mainTextEl.scrollHeight - mainTextEl.clientHeight;
                        if (scrollTop > maxScrollTop && maxScrollTop >= 0) {
                            mainTextEl.scrollTop = maxScrollTop;
                            (window as any).mainTextScrollbar.update();
                        }
                    }
                }
            });
            splitInstances.push(split1);
            splitInstancesMap.set('splitRoomPicPanel', split1);
            addDoubleClickReset(split1, [roomPanel, picPanel], [defaultSplitSizes.splitRoomPicPanel, 100 - defaultSplitSizes.splitRoomPicPanel], 'splitRoomPicPanel');
        }

        // 2. Split between mainrow and PlayerRow (vertical)
        const mainRow = document.querySelector('.mainrow') as HTMLElement;
        const playerRow = document.getElementById('PlayerRow');
        if (mainRow && playerRow) {
            const savedSize = PlayerSettings.splitMainPlayerRow;
            const split2 = Split([mainRow, playerRow], {
                direction: 'vertical',
                sizes: [savedSize, 100 - savedSize],
                minSize: [200, 100],
                gutterSize: 5,
                cursor: 'row-resize',
                onDragEnd: (sizes: number[]) => {
                    PlayerSettings.splitMainPlayerRow = sizes[0];
                    // Update Perfect Scrollbar after resize
                    const mainTextEl = document.getElementById('MainText');
                    if (mainTextEl && (window as any).mainTextScrollbar) {
                        (window as any).mainTextScrollbar.update();
                        // Ensure scroll position is valid
                        const scrollTop = mainTextEl.scrollTop;
                        const maxScrollTop = mainTextEl.scrollHeight - mainTextEl.clientHeight;
                        if (scrollTop > maxScrollTop && maxScrollTop >= 0) {
                            mainTextEl.scrollTop = maxScrollTop;
                            (window as any).mainTextScrollbar.update();
                        }
                    }
                }
            });
            splitInstances.push(split2);
            splitInstancesMap.set('splitMainPlayerRow', split2);
            addDoubleClickReset(split2, [mainRow, playerRow], [defaultSplitSizes.splitMainPlayerRow, 100 - defaultSplitSizes.splitMainPlayerRow], 'splitMainPlayerRow');
        }

        // 3. Split between compass-and-portrait and the three panels container (horizontal)
        // First, wrap the three panels in a container if needed
        const compassPortrait = document.querySelector('.compass-and-portrait');
        const roomObjectsPanel = document.getElementById('RoomObjectsPanel');
        const visibleCharsPanel = document.getElementById('VisibleCharactersPanel');
        const inventoryPanel = document.getElementById('InventoryPanel');
        const playerRowEl = document.getElementById('PlayerRow');
        
        if (compassPortrait && roomObjectsPanel && visibleCharsPanel && playerRowEl) {
            // Check if panels container already exists
            let panelsContainer = document.getElementById('player-row-panels-container');
            if (!panelsContainer) {
                // Create a wrapper container for the three panels
                panelsContainer = document.createElement('div');
                panelsContainer.id = 'player-row-panels-container';
                panelsContainer.style.display = 'flex';
                panelsContainer.style.flex = '1';
                
                // Insert the container after compass-and-portrait
                compassPortrait.parentNode!.insertBefore(panelsContainer, roomObjectsPanel);
                
                // Move the panels into the container
                panelsContainer.appendChild(roomObjectsPanel);
                panelsContainer.appendChild(visibleCharsPanel);
                if (inventoryPanel && !isInventoryHidden()) {
                    panelsContainer.appendChild(inventoryPanel);
                } else if (inventoryPanel && inventoryPanel.parentElement) {
                    inventoryPanel.parentElement.removeChild(inventoryPanel);
                }
            }
            
            // Split compass-and-portrait from panels container
            const savedCompassSize = getEffectiveCompassSplitSize(PlayerSettings.splitCompassRoomObjects);
            const split3 = Split([compassPortrait as HTMLElement, panelsContainer], {
                sizes: [savedCompassSize, 100 - savedCompassSize],
                minSize: [150, 200],
                gutterSize: 5,
                cursor: 'col-resize',
                onDragEnd: (sizes: number[]) => {
                    PlayerSettings.splitCompassRoomObjects = getStoredCompassSplitSize(sizes[0]);
                }
            });
            splitInstances.push(split3);
            splitInstancesMap.set('splitCompassRoomObjects', split3);
            const defaultCompassSize = getEffectiveCompassSplitSize(defaultSplitSizes.splitCompassRoomObjects);
            addDoubleClickReset(split3, [compassPortrait as HTMLElement, panelsContainer], [defaultCompassSize, 100 - defaultCompassSize], 'splitCompassRoomObjects');
            
            // Split the three panels within the container (3-way split)
            const savedRoomObjects = PlayerSettings.splitRoomObjectsVisibleChars;
            const savedVisibleChars = PlayerSettings.splitVisibleCharsInventory;
            // Calculate third panel size
            const thirdSize = 100 - savedRoomObjects - savedVisibleChars;
            const defaultThirdSize = 100 - defaultSplitSizes.splitRoomObjectsVisibleChars - defaultSplitSizes.splitVisibleCharsInventory;
            
            const panelElements = isInventoryHidden() || !inventoryPanel
                ? [roomObjectsPanel, visibleCharsPanel]
                : [roomObjectsPanel, visibleCharsPanel, inventoryPanel];
            roomVisibleSplitCount = panelElements.length;
            const split4 = Split(panelElements, {
                sizes: getEffectiveRoomVisibleSplitSizes().slice(0, roomVisibleSplitCount),
                minSize: roomVisibleSplitCount === 2 ? [100, 100] : [100, 100, 100],
                gutterSize: 5,
                cursor: 'col-resize',
                onDragEnd: (sizes: number[]) => {
                    const roomSize = sizes[0];
                    const visibleSize = sizes[1] ?? (100 - roomSize);
                    updateStoredRoomVisibleShares(roomSize, visibleSize);
                }
            });
            splitInstances.push(split4);
            splitInstancesMap.set('splitRoomObjectsVisibleChars', split4);
            const effectiveDefaults = getEffectiveRoomVisibleSplitSizes().slice(0, roomVisibleSplitCount);
            addDoubleClickReset(split4, panelElements, effectiveDefaults, 'splitRoomObjectsVisibleChars');
        }
        
        splitsInitialized = true;
    }

    // Function to apply settings changes
    function applySettingChange(name: string, value: any): void {
        const Settings = (window as any).Settings;
        const GameUI = (window as any).GameUI;
        const GameHistory = (window as any).GameHistory;
        const BGMusic = document.getElementById('BGMusic') as HTMLAudioElement;
        const SoundEffect = document.getElementById('SoundEffect') as HTMLAudioElement;
        const backButton = document.getElementById('back') as HTMLButtonElement;
        
        switch (name) {
            case 'splitRoomPicPanel': {
                const split = splitInstancesMap.get('splitRoomPicPanel');
                if (split && typeof split.setSizes === 'function') {
                    split.setSizes([value, 100 - value]);
                    // Update Perfect Scrollbar after resize
                    const mainTextEl = document.getElementById('MainText');
                    if (mainTextEl && (window as any).mainTextScrollbar) {
                        (window as any).mainTextScrollbar.update();
                    }
                }
                break;
            }
            case 'splitMainPlayerRow': {
                const split = splitInstancesMap.get('splitMainPlayerRow');
                if (split && typeof split.setSizes === 'function') {
                    split.setSizes([value, 100 - value]);
                    // Update Perfect Scrollbar after resize
                    const mainTextEl = document.getElementById('MainText');
                    if (mainTextEl && (window as any).mainTextScrollbar) {
                        (window as any).mainTextScrollbar.update();
                    }
                }
                break;
            }
            case 'splitCompassRoomObjects': {
                const split = splitInstancesMap.get('splitCompassRoomObjects');
                if (split && typeof split.setSizes === 'function') {
                    const effectiveSize = getEffectiveCompassSplitSize(value);
                    split.setSizes([effectiveSize, 100 - effectiveSize]);
                }
                break;
            }
            case 'splitRoomObjectsVisibleChars': {
                const split = splitInstancesMap.get('splitRoomObjectsVisibleChars');
                if (split && typeof split.setSizes === 'function') {
                    const visibleCharsSize = Settings?.splitVisibleCharsInventory ?? 33.33;
                    updateStoredRoomVisibleShares(value, visibleCharsSize);
                    const [roomShare, visibleShare, inventoryShare] = getEffectiveRoomVisibleSplitSizes();
                    if (roomVisibleSplitCount === 2) {
                        split.setSizes([roomShare, visibleShare]);
                    } else {
                        split.setSizes([roomShare, visibleShare, inventoryShare]);
                    }
                }
                break;
            }
            case 'splitVisibleCharsInventory': {
                const split = splitInstancesMap.get('splitRoomObjectsVisibleChars');
                if (split && typeof split.setSizes === 'function') {
                    const roomObjectsSize = Settings?.splitRoomObjectsVisibleChars ?? 33.33;
                    updateStoredRoomVisibleShares(roomObjectsSize, value);
                    const [roomShare, visibleShare, inventoryShare] = getEffectiveRoomVisibleSplitSizes();
                    if (roomVisibleSplitCount === 2) {
                        split.setSizes([roomShare, visibleShare]);
                    } else {
                        split.setSizes([roomShare, visibleShare, inventoryShare]);
                    }
                }
                break;
            }
            case 'darkMode': {
                if (GameUI) {
                    GameUI.setDarkMode(value);
                }
                break;
            }
            case 'musicVolume': {
                if (BGMusic) {
                    const volume = value / 100;
                    BGMusic.volume = volume;
                    BGMusic.muted = volume == 0;
                }
                break;
            }
            case 'sfxVolume': {
                if (SoundEffect) {
                    const volume = value / 100;
                    SoundEffect.volume = volume;
                    SoundEffect.muted = volume == 0;
                }
                break;
            }
            case 'historyEnabled': {
                if (GameHistory) {
                    GameHistory.enabled = value;
                    if (!value) {
                        GameHistory.reset();
                        if (backButton) backButton.disabled = true;
                    }
                    if (backButton) backButton.title = GameHistory.noGoBackReason();
                }
                break;
            }
            case 'historySize': {
                if (GameHistory) {
                    GameHistory.MAX_HISTORY_SIZE = value;
                }
                break;
            }
        }
    }

    // Initialize splits after a delay to ensure DOM is ready
    setTimeout(() => {
        initializeSplits();
        applyCompassSplitSizeFromSettings();
        applyRoomVisibleSplitFromSettings();
        
        // Note: Storage events are already handled by Settings class listeners
        // which update values and dispatch settingsChanged events
        // We just need to listen for settingsChanged events to apply the changes
        
        // Listen for custom settingsChanged events (same tab and cross-tab via storage listener)
        window.addEventListener('settingsChanged', ((e: CustomEvent) => {
            if (e.detail && e.detail.name && e.detail.value !== undefined) {
                applySettingChange(e.detail.name, e.detail.value);
            }
        }) as EventListener);
    }, 200);

    updatePortraitLayout = (): void => {
        applyCompassSplitSizeFromSettings();
    };

    updateInventoryLayout = (): void => {
        if (isInventoryHidden()) {
            const inventoryEl = document.getElementById('InventoryPanel');
            if (inventoryEl && inventoryEl.parentElement) {
                inventoryEl.parentElement.removeChild(inventoryEl);
            }
        }
        applyRoomVisibleSplitFromSettings();
    };

    isGameCompatible = (game: any): boolean => {
        const gameVersion = String(game?.RegaliaVersion || '').trim();
        if (isLegacyRegaliaVersion(gameVersion)) return true;
        if (compareSemver(gameVersion, REGALIA_PLAYER_VERSION) > 0) {
            (window as any).showAlert?.(
                'Unsupported Game Version',
                `This game requires Regalia ${gameVersion} or newer. You are running ${REGALIA_PLAYER_VERSION}.`
            );
            return false;
        }
        return true;
    };

    isSaveVersionMismatch = (savedGame: any): boolean => {
        if (!savedGame) return false;
        const savedTitle = String(savedGame.gameTitle || '').trim();
        const savedVersion = String(savedGame.gameVersion || '').trim();
        const currentTitle = String(TheGame?.Title || gameController.title() || '').trim();
        const currentVersion = String(TheGame?.GameVersion || '').trim();
        if (savedTitle && currentTitle && savedTitle !== currentTitle) return true;
        if (savedVersion && currentVersion && savedVersion !== currentVersion) return true;
        return false;
    };


    // Function to trigger compass direction movement
    function triggerCompassDirection(direction: string): void {
        // Don't allow movement if game is paused, awaiting input, or if input menus are visible
        if (gameController.gamePaused || gameController.gameAwaitingInput) {
            return;
        }
        
        // Check if any input menus are visible (use computed style, not inline style)
        const playernameChoice = $dom("#playernamechoice").get(0) as HTMLElement;
        const genderChoice = $dom("#genderchoice").get(0) as HTMLElement;
        const textChoice = $dom("#textchoice").get(0) as HTMLElement;
        const textActionChoice = $dom("#textactionchoice").get(0) as HTMLElement;
        const inputMenu = $dom("#inputmenu").get(0) as HTMLElement;
        const cmdInputMenu = $dom("#cmdinputmenu").get(0) as HTMLElement;
        
        function isElementVisible(el: HTMLElement | null): boolean {
            if (!el) return false;
            const style = window.getComputedStyle(el);
            return style.visibility !== 'hidden' && style.display !== 'none';
        }
        
        if (isElementVisible(playernameChoice)) return;
        if (isElementVisible(genderChoice)) return;
        if (isElementVisible(textChoice)) return;
        if (isElementVisible(textActionChoice)) return;
        if (isElementVisible(inputMenu)) return;
        if (isElementVisible(cmdInputMenu)) return;
        
        // Find the active compass direction element and click it
        const compassDir = document.querySelector(`.compass-direction.active[data-direction="${direction}"]`) as HTMLElement;
        if (compassDir) {
            compassDir.click();
        }
    }

    document.addEventListener('keydown', (e) => {
        // Handle Continue button when paused
        if ((e.key === 'Enter' || e.key === ' ') && gameController.gamePaused) {
            // Do not steal the keystrokes from text inputs
            if (typeof (e.target as any).setSelectionRange === 'function') return;

            const continueBtn = $dom("#Continue").get(0) as HTMLElement;
            if (continueBtn && !continueBtn.hasAttribute('disabled')) {
                e.preventDefault();
                e.stopPropagation();
                continueBtn.click();
                return;
            }
        }
        
        // Don't handle compass navigation if user is typing in an input field
        const activeEl = document.activeElement as HTMLElement | null;
        if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || (activeEl as any).isContentEditable)) {
            // Allow Enter/Space for Continue even in input fields if paused
            if (!gameController.gamePaused) {
                return;
            }
        }
        
        // Handle compass navigation
        let direction: string | null = null;
        
        // Check e.code first for numpad keys (works regardless of numlock state)
        // When numlock is OFF, numpad keys send arrow key events but e.code still identifies them as numpad
        if (e.code.startsWith("Numpad")) {
            switch (e.code) {
                case "Numpad2":
                    direction = "South";
                    break;
                case "Numpad4":
                    direction = "West";
                    break;
                case "Numpad6":
                    direction = "East";
                    break;
                case "Numpad8":
                    direction = "North";
                    break;
                case "Numpad1":
                    direction = "SouthWest";
                    break;
                case "Numpad3":
                    direction = "SouthEast";
                    break;
                case "Numpad7":
                    direction = "NorthWest";
                    break;
                case "Numpad9":
                    direction = "NorthEast";
                    break;
                case "Numpad5":
                    // Examine current location
                    e.preventDefault();
                    e.stopPropagation();
                    if (TheGame) {
                        const currentRoom = Finder.room(TheGame.Player.CurrentRoom);
                        if (currentRoom) {
                            const examineAction = Finder.action(currentRoom.Actions, "Examine");
                            if (examineAction) {
                                void gameController.executeAndRunTimersAsync(async () => {
                                    (window as any).ActionRecorder?.actedOnRoom?.("Examine");
                                    const mainText = $dom("#MainText");
                                    const mainTextEl = mainText.get(0);
                                    if (!mainTextEl) return;
                                    
                                    const isEmpty = mainTextEl.innerHTML.trim() === '';
                                    const scrollHeightBefore = mainTextEl.scrollHeight;
                                    
                                    let actionText = '';
                                    if (!isEmpty) {
                                        actionText = '<hr>';
                                    }
                                    actionText += '<br><b>Examine</b><br><br>';
                                    
                                    const processedText = processTextForOutput(actionText, isEmpty);
                                    mainText.append(processedText);
                                    
                                    setTimeout(() => {
                                        const scrollHeightAfter = mainTextEl.scrollHeight;
                                        const clientHeight = mainTextEl.clientHeight;
                                        const maxScrollTopAfter = scrollHeightAfter - clientHeight;
                                        
                                        if (scrollHeightAfter <= clientHeight) {
                                            mainTextEl.scrollTop = scrollHeightAfter;
                                        } else {
                                            const targetScrollTop = Math.min(scrollHeightBefore, maxScrollTopAfter);
                                            mainTextEl.scrollTop = targetScrollTop;
                                        }
                                        
                                        if ((window as any).mainTextScrollbar) {
                                            (window as any).mainTextScrollbar.update();
                                        }
                                    }, 0);
                                    
                                    ResetLoopObjects();
                                    if (TheGame) TheGame.TurnCount++;
                                    await GameActions.processActionAsync(examineAction, false, currentRoom as any);
                                    GameUI.onInteractionResume();
                                });
                            }
                        }
                    }
                    return;
                case "NumpadAdd":
                    if (!e.shiftKey) {
                        direction = "Down";
                    }
                    break;
                case "NumpadSubtract":
                    if (!e.shiftKey) {
                        direction = "Up";
                    }
                    break;
                case "NumpadDecimal":
                    direction = "Out";
                    break;
                case "Numpad0":
                    direction = "In";
                    break;
            }
        } else {
            // Regular arrow keys (not from numpad)
            switch (e.key) {
                case "ArrowUp":
                    direction = "North";
                    break;
                case "ArrowDown":
                    direction = "South";
                    break;
                case "ArrowLeft":
                    direction = "West";
                    break;
                case "ArrowRight":
                    direction = "East";
                    break;
            }
            
            // Handle regular navigation keys (not numpad)
            switch (e.code) {
                case "Home":
                    direction = "NorthWest";
                    break;
                case "PageUp":
                    direction = "NorthEast";
                    break;
                case "End":
                    direction = "SouthWest";
                    break;
                case "PageDown":
                    direction = "SouthEast";
                    break;
                case "Delete":
                    // Only handle Delete if it's not from numpad
                    if (e.location !== KeyboardEvent.DOM_KEY_LOCATION_NUMPAD) {
                        direction = "Out";
                    }
                    break;
                case "Insert":
                    // Only handle Insert if it's not from numpad
                    if (e.location !== KeyboardEvent.DOM_KEY_LOCATION_NUMPAD) {
                        direction = "In";
                    }
                    break;
            }
        }
        
        if (direction) {
            e.preventDefault();
            e.stopPropagation();
            triggerCompassDirection(direction);
            return;
        }
        
        // Handle other special keys
        switch (e.code) {
            case "F8":
                if (!GameUI.saveDisabled && !gameController.gameAwaitingInput) {
                    (window as any).handleFileSave?.(true);
                }
                break;
            case "F9":
                if (!gameController.gameAwaitingInput) {
                    (window as any).handleFileSelect?.(true);
                }
                break;
        }
    });

    document.addEventListener('mousemove', (e) => {
        (window as any).x = e.pageX;
        (window as any).y = e.pageY;
    });

    $dom("#Continue").click(async (e) => {
        ActionRecorder.clickedContinue();
        const continueBtn = $dom("#Continue").get(0) as HTMLElement;
        if (continueBtn && !continueBtn.hasAttribute('disabled')) {
            const mainTextEl = $dom('#MainText').get(0) as HTMLElement | null;
            if (mainTextEl && mainTextEl.innerHTML.trim() !== '' && !mainTextEndsWithTurnSeparator(mainTextEl)) {
                const separator = document.createElement('hr');
                separator.className = 'turn-separator';
                mainTextEl.appendChild(separator);
            }
            continueBtn.setAttribute('disabled', 'disabled');
            // If a CT_PAUSEGAME chain was suspended, resuming it hands control
            // back to that chain; its caller runs the timer events when it
            // finishes. Running timers here as well would execute them
            // concurrently with the resumed chain, and the two chains would
            // pop each other's Globals frames (e.g. losing the frame of a
            // following CT_SETVARIABLEBYINPUT, freezing the input dialog).
            const resumed = gameController.continue();
            if (!resumed) {
                await gameController.runCommandsAndCheckTimers();
                GameUI.onInteractionResume();
            }
        }
    });

    $dom("#PlayerImg").click((e) => {
        const target = e.currentTarget as HTMLElement;
        if (target.dataset.disabled) return;

        if (TheGame) {
            Globals.theObj = TheGame.Player;
            GameUI.displayActions(TheGame.Player, e as MouseEvent);
        }
    });

    $dom("#RoomThumbImg").click((e) => {
        // Disabled while a pause or input prompt is pending (hideGameElements);
        // starting a room action then would run concurrently with that chain.
        if ((e.currentTarget as HTMLElement).dataset.disabled) return;
        if (TheGame) {
            Globals.theObj = Finder.room(TheGame.Player.CurrentRoom);
            if (Globals.theObj) {
                GameUI.displayActions(Globals.theObj, e as MouseEvent);
            }
        }
    });

    function onKeyupEnter(selector: string, fn: () => void | Promise<void>): void {
        $dom(selector).on('keyup', (e: Event) => {
            const keyEvent = e as KeyboardEvent;
            if (keyEvent.key === "Enter") {
                fn();
            }
        });
    }

    async function setTextInputChoice(): Promise<void> {
        const input = $dom("#textinput").get(0) as HTMLInputElement;
        const selectedObj = input?.value;
        if (selectedObj != null) {
            if (input) input.value = '';
            $dom("#textchoice").css("visibility", "hidden");
            ActionRecorder.filledInTextInput(String(selectedObj));
            maybeAppendTurnSeparatorForInput();
            (window as any).SetCommandInput?.(Globals.variableGettingSet, selectedObj);
            gameController.stopAwaitingInput();
            // Explicitly resume the suspended chain (CT_SETVARIABLEBYINPUT's awaiter
            // on the inputPromises stack). If there was a suspended chain, it owns
            // continuing the action AND running timer events when it finishes — so
            // we must NOT also call runCommandsAndCheckTimers here, or timers fire
            // twice and the commands after CT_EXECUTETIMER get stranded.
            const resumed = gameController.resumeExecution();
            if (!resumed) {
                // Legacy path: nothing was awaiting. Drive timers ourselves.
                await gameController.runCommandsAndCheckTimers();
                GameUI.onInteractionResume();
            }
        }
    }
    $dom("#textbutton").click(setTextInputChoice);
    onKeyupEnter('#textinput', setTextInputChoice);

    function setPlayerNameChoice(): void {
        $dom("#playernamechoice").css("visibility", "hidden");
        gameController.stopAwaitingInput();
        const input = $dom("#playernametext").get(0) as HTMLInputElement;
        const newname = input?.value || '';
        if (TheGame) {
            TheGame.Player.Name = newname.trim();
            if (TheGame.Player.bPromptForGender) {
                gameController.startAwaitingInput();
                $dom("#genderchoice").css("visibility", "visible");
                GameUI.hideGameElements();
                // Focus first gender option and set up keyboard navigation
                setTimeout(() => {
                    setupGenderSelection();
                }, 0);
            } else {
                (window as any).StartGame?.();
            }
        }
    }
    
    function setupGenderSelection(): void {
        const genderOptions = document.querySelectorAll('.genderchoiceSelect') as NodeListOf<HTMLElement>;
        
        // Make all options focusable
        genderOptions.forEach((option) => {
            option.setAttribute('tabindex', '0');
            option.setAttribute('role', 'button');
            option.classList.remove('selected');
        });
        
        // Pre-select first option
        if (genderOptions.length > 0) {
            genderOptions[0].classList.add('selected');
            // Focus the container so keyboard events are captured
            const genderContainer = document.getElementById('genderchoice');
            if (genderContainer) {
                genderContainer.setAttribute('tabindex', '0');
                genderContainer.focus();
            }
        }
        
        // Handle keyboard navigation - listen on document but check if gender menu is visible
        const handleKeyDown = (e: KeyboardEvent) => {
            // Only handle if gender choice is visible
            const genderChoice = document.getElementById('genderchoice');
            if (!genderChoice || genderChoice.style.visibility === 'hidden' || genderChoice.style.display === 'none') {
                return;
            }
            
            // Don't handle if user is typing in an input field
            const activeEl = document.activeElement as HTMLElement | null;
            if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || (activeEl as any).isContentEditable)) {
                return;
            }
            
            const options = Array.from(genderOptions);
            const currentSelected = genderChoice.querySelector('.genderchoiceSelect.selected') as HTMLElement;
            const currentIndex = currentSelected ? options.indexOf(currentSelected) : 0;
            
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                e.stopPropagation();
                const nextIndex = (currentIndex + 1) % options.length;
                if (currentSelected) {
                    currentSelected.classList.remove('selected');
                }
                options[nextIndex].classList.add('selected');
                options[nextIndex].focus();
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                e.stopPropagation();
                const prevIndex = (currentIndex - 1 + options.length) % options.length;
                if (currentSelected) {
                    currentSelected.classList.remove('selected');
                }
                options[prevIndex].classList.add('selected');
                options[prevIndex].focus();
            } else if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                const selected = genderChoice.querySelector('.genderchoiceSelect.selected') as HTMLElement;
                if (selected) {
                    selected.click();
                }
            }
        };
        
        // Remove old listeners and add new one
        document.removeEventListener('keydown', handleKeyDown as any);
        document.addEventListener('keydown', handleKeyDown as any, true); // Use capture phase
    }
    
    $dom("#playernamebutton").click(setPlayerNameChoice);
    onKeyupEnter('#playernametext', setPlayerNameChoice);

    function formatDate(date: Date): string {
        const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

        return [
            weekdays[date.getDay()],
            months[date.getMonth()],
            date.getDate(),
            [
                date.getHours().toString().padStart(2, '0'),
                date.getMinutes().toString().padStart(2, '0'),
                date.getSeconds().toString().padStart(2, '0')
            ].join(':')
        ].join(' ');
    }

    function addSavesToTable(tbody: HTMLElement): void {
        function updateSaveName(saveName: HTMLElement): void {
            (saveName as HTMLTextAreaElement).style.height = "auto";
            const height = saveName.scrollHeight;
            if (height > 0) (saveName as HTMLTextAreaElement).style.height = height + "px";
        }

        SavedGamesModule.getSortedSaves().then((savedGames) => {
            savedGames.forEach((savedGame) => {
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td><button class="btn load-save">Load</button></td>
                <td>${savedGame.id}</td>
                <td><textarea class="save-name" rows="1" spellcheck="false"></textarea><button class="btn rename-save" disabled>Rename</button></td>
                <td>${formatDate(new Date(savedGame.date))}</td>
                <td><button class="btn overwrite-save">Overwrite Save</button></td>
                <td><button class="btn btn-danger destroy-save">Destroy</button></td>
            `;

            const overwriteBtn = tr.querySelector('button.overwrite-save') as HTMLButtonElement;
            if (overwriteBtn) {
                overwriteBtn.setAttribute('data-save-id', String(savedGame.id));
                overwriteBtn.setAttribute('data-save-name', savedGame.name);
            }

            const loadBtn = tr.querySelector('button.load-save') as HTMLElement;
            if (loadBtn) {
                loadBtn.setAttribute('data-save-id', String(savedGame.id));
            }

            const destroyBtn = tr.querySelector('button.destroy-save') as HTMLElement;
            if (destroyBtn) {
                destroyBtn.setAttribute('data-save-id', String(savedGame.id));
            }

            const renameBtn = tr.querySelector('button.rename-save') as HTMLButtonElement;
            const saveName = tr.querySelector('textarea.save-name') as HTMLTextAreaElement;
            if (saveName) {
                saveName.value = savedGame.name;
                saveName.addEventListener('input', function() {
                    updateSaveName(this);
                    if (renameBtn) renameBtn.disabled = false;
                });
                saveName.addEventListener('keydown', function(e: KeyboardEvent) {
                    if (e.key == 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (renameBtn) {
                            renameBtn.focus();
                            renameBtn.click();
                        }
                    }
                });
                window.setTimeout(() => updateSaveName(saveName), 0);
            }

            if (renameBtn) {
                renameBtn.addEventListener('click', () => {
                    if (saveName) {
                        SavedGamesModule.renameSave(savedGame.id, saveName.value).then(() => {
                            renameBtn.disabled = true;
                            if (overwriteBtn) overwriteBtn.setAttribute('data-save-name', saveName.value);
                        }).catch((err) => {
                            console.error('Failed to rename save:', err);
                        });
                    }
                });
            }

            // Disable overwriting/renaming for quick save
            if (savedGame.id == 0) {
                if (renameBtn) {
                    renameBtn.disabled = true;
                    renameBtn.onclick = null;
                }
                if (saveName) saveName.disabled = true;
                if (overwriteBtn) {
                    overwriteBtn.disabled = true;
                    overwriteBtn.onclick = null;
                }
            }

            tbody.appendChild(tr);
            });

            const table = tbody.closest('table');
            if (table) {
                $dom(table).toggle(savedGames.length > 0);
            }
        }).catch((err) => {
            console.error('Failed to load saves:', err);
        });
    }

    $dom("#new_savegame").on('click', () => {
        hideSaveAndLoadMenus();
        (window as any).handleFileSave?.(false, true);
    });

    $dom(".destroy_savegames").on('click', () => {
        (window as any).handleDestroyAllSaves?.();
    });

    // Export/Import dialogs are now handled by SaveLoadDialogContent React component

    // Save/Load Dialog using React
    const saveBtn = $dom("#save").get(0);
    if (saveBtn) {
        $dom(saveBtn).click(() => {
            // Don't allow save/load when input is requested
            if (gameController.gameAwaitingInput) return;
            const dialogRoot = document.getElementById('save-load-dialog-root');
            if (dialogRoot && (window as any).React && (window as any).ReactDOM) {
                const React = (window as any).React;
                const ReactDOM = (window as any).ReactDOM;
                const Dialog = (window as any).Dialog;
                const SaveLoadDialogContent = (window as any).SaveLoadDialogContent;
                
                if (Dialog && SaveLoadDialogContent) {
                    if (ReactDOM.createRoot) {
                        let root = (window as any).saveLoadDialogRoot;
                        if (!root) {
                            root = ReactDOM.createRoot(dialogRoot);
                            (window as any).saveLoadDialogRoot = root;
                        }
                        root.render(React.createElement(Dialog, {
                            title: 'Game Saves',
                            onClose: () => {
                                root.render(null);
                            },
                            closeOnBackdropClick: true
                        }, React.createElement(SaveLoadDialogContent)));
                    } else {
                        ReactDOM.render(
                            React.createElement(Dialog, {
                                title: 'Game Saves',
                                onClose: () => {
                                    ReactDOM.render(null, dialogRoot);
                                },
                                closeOnBackdropClick: true
                            }, React.createElement(SaveLoadDialogContent)),
                            dialogRoot
                        );
                    }
                }
            }
        });
    }

    const downloadButton = $dom("#download_button").get(0) as HTMLButtonElement;
    if (downloadButton) {
        if (localFileContext) {
            downloadButton.disabled = true;
            downloadButton.title = 'Download unavailable in local file context';
            downloadButton.style.display = 'none';
        } else {
            $dom(downloadButton).on('click', () => {
                if (gameController.gameAwaitingInput) return;
                const dialogRoot = document.getElementById('download-dialog-root');
                if (dialogRoot && (window as any).React && (window as any).ReactDOM) {
                    const React = (window as any).React;
                    const ReactDOM = (window as any).ReactDOM;
                    const Dialog = (window as any).Dialog;
                    const DownloadPackageDialog = (window as any).DownloadPackageDialog;

                    if (Dialog && DownloadPackageDialog) {
                        const handleClose = () => {
                            if (ReactDOM.createRoot) {
                                const root = (window as any).downloadDialogRoot;
                                if (root) {
                                    root.render(null);
                                }
                            } else {
                                ReactDOM.render(null, dialogRoot);
                            }
                        };

                        if (ReactDOM.createRoot) {
                            let root = (window as any).downloadDialogRoot;
                            if (!root) {
                                root = ReactDOM.createRoot(dialogRoot);
                                (window as any).downloadDialogRoot = root;
                            }
                            root.render(React.createElement(Dialog, {
                                title: 'Download Game',
                                onClose: handleClose,
                                closeOnBackdropClick: false,
                                maxWidth: '520px'
                            }, React.createElement(DownloadPackageDialog, { onClose: handleClose })));
                        } else {
                            ReactDOM.render(
                                React.createElement(Dialog, {
                                    title: 'Download Game',
                                    onClose: handleClose,
                                    closeOnBackdropClick: false,
                                    maxWidth: '520px'
                                }, React.createElement(DownloadPackageDialog, { onClose: handleClose })),
                                dialogRoot
                            );
                        }
                    }
                }
            });
        }
    }

    $dom("div.genderchoiceSelect").click((e) => {
        const target = e.currentTarget as HTMLElement;
        selectGenderOption(target);
    });
    
    function selectGenderOption(target: HTMLElement): void {
        // Remove selected class from all options
        document.querySelectorAll('.genderchoiceSelect').forEach(opt => {
            opt.classList.remove('selected');
        });
        // Add selected class to clicked option
        target.classList.add('selected');
        
        const gender = target.getAttribute('value');
        if (gender != null && TheGame) {
            TheGame.Player.PlayerGender = gender;
            gameController.stopAwaitingInput();
            $dom("#genderchoice").css("visibility", "hidden");
            (window as any).StartGame?.();
        }
    }

    async function setTextActionChoice(): Promise<void> {
        const input = $dom("#textactioninput").get(0) as HTMLInputElement;
        const selectedObj = input?.value;
        if (selectedObj != null) {
            if (input) input.value = '';
            Globals.additionalData = selectedObj;
            gameController.stopAwaitingInput();
            $dom("#textactionchoice").css("visibility", "hidden");
            maybeAppendTurnSeparatorForInput();
            if (getObjectClass(Globals.inputDataObject) == "action" || ("actionparent" in Globals.inputDataObject)) {
                await GameActions.executeActionAsync(Globals.inputDataObject, Globals.bMasterTimer);
                await gameController.runCommandsAndCheckTimers();
                GameUI.onInteractionResume();
            }
        }
    }

    $dom("#textactionbutton").click(setTextActionChoice);
    onKeyupEnter('#textactioninput', setTextActionChoice);

    $dom("#textactioncancel").click(async () => {
        $dom("#textactionchoice").css("visibility", "hidden");
        Globals.additionalData = null;
        await gameController.stopAwaitingInputAsync();
    });

    $dom("#CancelInput").click(async () => {
        $dom("#inputmenu").css("visibility", "hidden");
        Globals.additionalData = null;
        gameController.stopAwaitingInput();
        const resumed = gameController.resumeExecution();
        if (!resumed) {
            await gameController.runCommandsAndCheckTimers();
        }
    });

    $dom("#cmdCancelInput").click(async () => {
        $dom("#cmdinputmenu").css("visibility", "hidden");
        Globals.additionalData = null;
        gameController.stopAwaitingInput();
        const resumed = gameController.resumeExecution();
        if (!resumed) {
            await gameController.runCommandsAndCheckTimers();
        }
    });

    $dom("#selectionmenu").on('focusout', () => {
        $dom("#selectionmenu").css("visibility", "hidden");
    });

    // Start live timer system with setTimeout-based scheduling
    GameTimers.startCountdownDisplay();
    GameTimers.rescheduleAllLiveTimers();

    // Add right-click support for selection menu triggers
    // Right-click on objects/characters/inventory/player/room opens selection menu
    // Unless Ctrl or Meta is held (then show browser context menu)
    document.addEventListener('contextmenu', (e: MouseEvent) => {
        // If Ctrl or Meta is held, allow normal context menu
        if (e.ctrlKey || e.metaKey) {
            return;
        }
        
        const target = e.target as HTMLElement;
        
        // Check if target is a clickable game element
        const isRoomObject = target.classList.contains('RoomObjects') || target.closest('.RoomObjects');
        const isVisibleCharacter = target.classList.contains('VisibleCharacters') || target.closest('.VisibleCharacters');
        const isPlayerImg = target.id === 'PlayerImg' || target.closest('#PlayerImg');
        const isRoomThumb = target.id === 'RoomThumbImg' || target.closest('#RoomThumbImg');
        
        if (isRoomObject || isVisibleCharacter || isPlayerImg || isRoomThumb) {
            e.preventDefault();
            e.stopPropagation();
            
            // Create a synthetic click event with the same coordinates as the right-click
            const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
                button: 0,
                clientX: e.clientX,
                clientY: e.clientY
            });
            
            // Trigger the click handler to show the selection menu
            if (isRoomObject || isVisibleCharacter) {
                const el = (target.classList.contains('RoomObjects') || target.classList.contains('VisibleCharacters')) ? target : (target.closest('.RoomObjects') || target.closest('.VisibleCharacters'));
                if (el) el.dispatchEvent(clickEvent);
            } else if (isPlayerImg) {
                const playerImg = target.id === 'PlayerImg' ? target : target.closest('#PlayerImg');
                if (playerImg) {
                    (playerImg as HTMLElement).dispatchEvent(clickEvent);
                }
            } else if (isRoomThumb) {
                const roomThumb = target.id === 'RoomThumbImg' ? target : target.closest('#RoomThumbImg');
                if (roomThumb) {
                    (roomThumb as HTMLElement).dispatchEvent(clickEvent);
                }
            }
        }
    }, true); // Use capture phase to catch before other handlers

    $dom(".compass-direction").on('click', async (e) => {
        const target = e.currentTarget as HTMLElement;
        if (!target.classList.contains('active') || target.dataset.disabled) {
            return;
        }
        const direction = target.getAttribute('data-direction');
        if (!direction || !TheGame) return;
        const newRoom = GetDestinationRoomName(direction);
        if (!newRoom) return;
        TheGame.TurnCount++;
        ResetLoopObjects();
        Globals.bCancelMove = false;
        ActionRecorder.locationChange(direction);
        const curroom = Finder.room(TheGame.Player.CurrentRoom);

        if (curroom != null && !curroom.bLeaveFirstTime) {
            curroom.bLeaveFirstTime = true;
            await GameActions.runEventsAsync("<<On Player Leave First Time>>");
        }

        if (curroom != null) {
            await GameActions.runEventsAsync("<<On Player Leave>>");
        }

        if (!Globals.bCancelMove) {
            // Await the room's enter events, description and timer tick so the
            // end-of-turn work below (turn separator, status bar, history
            // snapshot for Back) happens after them, not before.
            await ChangeRoomAsync(Finder.room(newRoom) || null, true, true);
        }
        GameUI.onInteractionResume();
    });

    $dom(".compass-direction").hover(
        (e) => {
            const target = e.currentTarget as HTMLElement;
            if (!target.classList.contains('active')) {
                return;
            }
            const direction = target.getAttribute('data-direction');
            if (!direction) return;
            const destName = GetDestinationRoomName(direction);
            const destRoom = destName ? Finder.room(destName) : undefined;
            if (destRoom) {
                $dom("#tooltip").text(roomDisplayName(destRoom));
                $dom("#tooltip").css({
                    "left": ((window as any).x + 10) + "px",
                    "top": (window as any).y + "px",
                    "visibility": "visible"
                });
            }
        },
        () => {
            $dom("#tooltip").css("visibility", "hidden");
        }
    );

    const backButton = $dom("#back").get(0) as HTMLButtonElement;
    if (backButton) {
        $dom(backButton).click(() => {
            // Don't allow back when input is requested
            if (gameController.gameAwaitingInput) return;
            GameHistory.popState();
            backButton.disabled = !GameHistory.canGoBack();
            backButton.title = GameHistory.noGoBackReason();
        });
    }

    /* Options dialog setup */
    {
        const backdrop = $dom(".options-menu").get(0);
        const menu = $dom('.options-menu-content').get(0);
        const optionsForm = document.getElementById('options-form') as HTMLFormElement;

        if (backdrop) {
            $dom(backdrop).on('click', () => {
                $dom(backdrop).addClass("hidden");
            });
        }

        function setVolume(audioEl: HTMLAudioElement | null, volume: number): void {
            if (!audioEl) return;
            volume /= 100;
            audioEl.volume = volume;
            audioEl.muted = volume == 0;
        }

        const BGMusic = document.getElementById('BGMusic') as HTMLAudioElement;
        const SoundEffect = document.getElementById('SoundEffect') as HTMLAudioElement;

        setVolume(BGMusic, PlayerSettings.musicVolume);
        setVolume(SoundEffect, PlayerSettings.sfxVolume);
        GameHistory.enabled = PlayerSettings.historyEnabled;
        GameHistory.MAX_HISTORY_SIZE = PlayerSettings.historySize;

        function onOptionChange(e: Event): void {
            const field = (e.target as HTMLElement).closest('input') as HTMLInputElement;
            if (!field) return;
            switch (field.name) {
                case 'music-volume':
                case 'music-volume-slider': {
                    const value = parseInt(field.value);
                    setVolume(BGMusic, value);
                    PlayerSettings.musicVolume = value;
                    const namedElements = optionsForm.elements as any;
                    const otherField = namedElements[field.name === 'music-volume' ? 'music-volume-slider' : 'music-volume'] as HTMLInputElement;
                    if (otherField) otherField.value = String(value);
                    break;
                }

                case 'sfx-volume':
                case 'sfx-volume-slider': {
                    const value = parseInt(field.value);
                    setVolume(SoundEffect, value);
                    PlayerSettings.sfxVolume = value;
                    const namedElements = optionsForm.elements as any;
                    const otherField = namedElements[field.name === 'sfx-volume' ? 'sfx-volume-slider' : 'sfx-volume'] as HTMLInputElement;
                    if (otherField) otherField.value = String(value);
                    break;
                }

                case 'enable-history': {
                    if (PlayerSettings.historyEnabled !== field.checked) {
                        GameHistory.enabled = field.checked;
                        if (!field.checked) {
                            GameHistory.reset();
                            if (backButton) backButton.disabled = true;
                        }
                        if (backButton) backButton.title = GameHistory.noGoBackReason();
                        PlayerSettings.historyEnabled = field.checked;
                    }
                    break;
                }

                case 'history-size': {
                    const value = parseInt(field.value);
                    PlayerSettings.historySize = value;
                    GameHistory.MAX_HISTORY_SIZE = value;
                    break;
                }

                case 'enable-debug': {
                    PlayerSettings.debugEnabled = field.checked;
                    break;
                }

                case 'enable-darkmode': {
                    PlayerSettings.darkMode = field.checked;
                    GameUI.setDarkMode(PlayerSettings.darkMode);
                    break;
                }
            }
        }

        if (optionsForm) {
            $dom(optionsForm)
                .on('change', onOptionChange)
                .on('input', (e) => {
                    const field = e.currentTarget as HTMLInputElement;
                    if (field.name.endsWith('-slider')) onOptionChange(e);
                })
                .on('submit', (e) => {
                    e.preventDefault();
                    if (backdrop) $dom(backdrop).addClass("hidden");
                });
        }

        const optionsButton = $dom("#options_button").get(0);
        if (optionsButton && optionsForm) {
            $dom(optionsButton).on('click', () => {
                // Don't allow options when input is requested
                if (gameController.gameAwaitingInput) return;
                const namedElements = optionsForm.elements as any;
                const musicVol = namedElements['music-volume'] as HTMLInputElement;
                const musicSlider = namedElements['music-volume-slider'] as HTMLInputElement;
                const sfxVol = namedElements['sfx-volume'] as HTMLInputElement;
                const sfxSlider = namedElements['sfx-volume-slider'] as HTMLInputElement;
                const enableHistory = namedElements['enable-history'] as HTMLInputElement;
                const historySize = namedElements['history-size'] as HTMLInputElement;
                const enableDebug = namedElements['enable-debug'] as HTMLInputElement;
                const enableDarkmode = namedElements['enable-darkmode'] as HTMLInputElement;

                if (musicVol) musicVol.value = String(PlayerSettings.musicVolume);
                if (musicSlider) musicSlider.value = String(PlayerSettings.musicVolume);
                if (sfxVol) sfxVol.value = String(PlayerSettings.sfxVolume);
                if (sfxSlider) sfxSlider.value = String(PlayerSettings.sfxVolume);
                if (enableHistory) enableHistory.checked = PlayerSettings.historyEnabled;
                if (historySize) historySize.value = String(PlayerSettings.historySize);
                if (enableDebug) enableDebug.checked = PlayerSettings.debugEnabled;
                if (enableDarkmode) enableDarkmode.checked = PlayerSettings.darkMode;

                // Render Options Dialog using React
                const dialogRoot = document.getElementById('options-dialog-root');
                if (dialogRoot && (window as any).React && (window as any).ReactDOM) {
                    const React = (window as any).React;
                    const ReactDOM = (window as any).ReactDOM;
                    const Dialog = (window as any).Dialog;
                    
                    if (Dialog) {
                        const handleClose = () => {
                            if (ReactDOM.createRoot) {
                                let root = (window as any).optionsDialogRoot;
                                if (root) {
                                    root.render(null);
                                }
                            } else {
                                ReactDOM.render(null, dialogRoot);
                            }
                        };

                        if (ReactDOM.createRoot) {
                            let root = (window as any).optionsDialogRoot;
                            if (!root) {
                                root = ReactDOM.createRoot(dialogRoot);
                                (window as any).optionsDialogRoot = root;
                            }
                            const OptionsDialogContent = (window as any).OptionsDialogContent;
                            const ReactComponent = React.Component;
                            if (OptionsDialogContent) {
                                // Create a wrapper to manage save/cancel
                                const Wrapper = class extends ReactComponent<{ onSave?: () => void; onCancel?: () => void }, {}> {
                                    private contentRef: any = null;

                                    render() {
                                        return React.createElement(OptionsDialogContent, {
                                            ref: (instance: any) => {
                                                this.contentRef = instance;
                                                (window as any).__optionsDialogInstance = instance;
                                            },
                                            onSave: this.props.onSave,
                                            onCancel: this.props.onCancel
                                        });
                                    }
                                };

                                const handleSaveClick = () => {
                                    const instance = (window as any).__optionsDialogInstance;
                                    if (instance && typeof instance.handleSave === 'function') {
                                        instance.handleSave();
                                    }
                                    handleClose();
                                };

                                const handleCancelClick = () => {
                                    const instance = (window as any).__optionsDialogInstance;
                                    if (instance && typeof instance.handleCancel === 'function') {
                                        instance.handleCancel();
                                    }
                                    handleClose();
                                };

                                root.render(React.createElement(Dialog, {
                                    title: 'Options',
                                    onClose: handleClose,
                                    actionButtons: React.createElement(React.Fragment, null,
                                        React.createElement('button', {
                                            className: 'btn',
                                            onClick: handleCancelClick
                                        }, 'Cancel'),
                                        React.createElement('button', {
                                            className: 'btn',
                                            onClick: handleSaveClick
                                        }, 'Save')
                                    ),
                                    closeOnBackdropClick: false
                                }, React.createElement(Wrapper, {
                                    onSave: handleClose,
                                    onCancel: handleClose
                                })));
                            }
                        } else {
                            const OptionsDialogContent = (window as any).OptionsDialogContent;
                            const ReactComponent = React.Component;
                            if (OptionsDialogContent) {
                                const Wrapper = class extends ReactComponent<{ onSave?: () => void; onCancel?: () => void }, {}> {
                                    private contentRef: any = null;

                                    render() {
                                        return React.createElement(OptionsDialogContent, {
                                            ref: (instance: any) => {
                                                this.contentRef = instance;
                                                (window as any).__optionsDialogInstance = instance;
                                            },
                                            onSave: this.props.onSave,
                                            onCancel: this.props.onCancel
                                        });
                                    }
                                };

                                const handleSaveClick = () => {
                                    const instance = (window as any).__optionsDialogInstance;
                                    if (instance && typeof instance.handleSave === 'function') {
                                        instance.handleSave();
                                    }
                                    setTimeout(handleClose, 100);
                                };

                                const handleCancelClick = () => {
                                    const instance = (window as any).__optionsDialogInstance;
                                    if (instance && typeof instance.handleCancel === 'function') {
                                        instance.handleCancel();
                                    }
                                    handleClose();
                                };

                                ReactDOM.render(
                                    React.createElement(Dialog, {
                                        title: 'Options',
                                        onClose: handleClose,
                                        actionButtons: React.createElement(React.Fragment, null,
                                            React.createElement('button', {
                                                className: 'btn',
                                                onClick: handleCancelClick
                                            }, 'Cancel'),
                                            React.createElement('button', {
                                                className: 'btn',
                                                onClick: handleSaveClick
                                            }, 'Save')
                                        ),
                                    closeOnBackdropClick: false
                                }, React.createElement(Wrapper, {
                                    onSave: handleClose,
                                    onCancel: handleClose
                                })),
                                    dialogRoot
                                );
                            }
                        }
                    }
                }
            });
        }
    }

    if (backButton) {
        backButton.title = GameHistory.noGoBackReason();
    }

    GameUI.setDarkMode(PlayerSettings.darkMode);

    (window as any).receivedText?.();
});

// Make functions globally available
(window as any).GetGameImage = function (imageName: string): any {
    if (!TheGame) return null;
    const lowerCaseImageName = imageName.toLowerCase();
    return TheGame.Images.find((image: any) => {
        return image.TheName.toLowerCase() === lowerCaseImageName;
    });
};

(window as any).GetDestinationRoomName = GetDestinationRoomName;
(window as any).roomDisplayName = roomDisplayName;

(window as any).handleFileSave = function (bQuick: boolean, bNew?: boolean, CurID?: number, oldSaveName?: string, onSaveComplete?: () => void): void {
    const curdate = new Date();
    if (bQuick) {
        SavedGamesModule.createSave(0, 'QuickSave', curdate.toISOString(), SavedGamesModule.saveDataFor(TheGame!)).then(() => {
            GameUI.showMessage('Quick Saved', { type: 'success', timeout: 3.0 });
            Globals.hasUnsavedProgress = false;
            Globals.commandQueueEmptiedSinceCheckpoint = true;
            if (onSaveComplete) onSaveComplete();
        }).catch((err) => {
            console.error('Failed to save:', err);
            GameUI.showMessage('Save Failed', { type: 'error', timeout: 3.0 });
        });
    } else if (CurID !== undefined && oldSaveName) {
        // Overwrite existing save - directly save without dialog
        SavedGamesModule.createSave(CurID, oldSaveName, curdate.toISOString(), SavedGamesModule.saveDataFor(TheGame!)).then(() => {
            GameUI.showMessage('Game Saved', { type: 'success', timeout: 3.0 });
            Globals.hasUnsavedProgress = false;
            Globals.commandQueueEmptiedSinceCheckpoint = true;
            if (onSaveComplete) onSaveComplete();
        }).catch((err) => {
            console.error('Failed to save:', err);
            GameUI.showMessage('Save Failed', { type: 'error', timeout: 3.0 });
        });
    } else {
        // New save - show dialog for save name
        const dialogRoot = document.getElementById('save-name-dialog-root');
        if (!dialogRoot) return;

        const React = (window as any).React;
        const ReactDOM = (window as any).ReactDOM;
        const SaveNameDialog = (window as any).SaveNameDialog;
        if (!React || !ReactDOM || !SaveNameDialog) return;

        const handleSave = async (saveName: string) => {
            try {
                const nextId = bNew ? await SavedGamesModule.nextSaveId() : (CurID || 0);
                await SavedGamesModule.createSave(nextId, saveName, curdate.toISOString(), SavedGamesModule.saveDataFor(TheGame!));
                GameUI.showMessage('Game Saved', { type: 'success', timeout: 3.0 });
                Globals.hasUnsavedProgress = false;
                Globals.commandQueueEmptiedSinceCheckpoint = true;
                
                // Close dialog
                if (ReactDOM.createRoot) {
                    const root = (window as any).saveNameDialogRoot;
                    if (root) root.render(null);
                } else {
                    ReactDOM.render(null, dialogRoot);
                }
                
                // Call callback to close game saves dialog if provided (only for new saves)
                if (onSaveComplete && bNew) {
                    onSaveComplete();
                }
            } catch (err) {
                console.error('Failed to save:', err);
                GameUI.showMessage('Save Failed', { type: 'error', timeout: 3.0 });
            }
        };

        const handleCancel = () => {
            // Close dialog (but don't close game saves dialog)
            if (ReactDOM.createRoot) {
                const root = (window as any).saveNameDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        if (ReactDOM.createRoot) {
            let root = (window as any).saveNameDialogRoot;
            if (!root) {
                root = ReactDOM.createRoot(dialogRoot);
                (window as any).saveNameDialogRoot = root;
            }
            root.render(React.createElement(SaveNameDialog, {
                defaultValue: oldSaveName || '',
                onSave: handleSave,
                onCancel: handleCancel
            }));
        } else {
            ReactDOM.render(
                React.createElement(SaveNameDialog, {
                    defaultValue: oldSaveName || '',
                    onSave: handleSave,
                    onCancel: handleCancel
                }),
                dialogRoot
            );
        }
    }
};

(window as any).handleFileSelect = async function (bQuick: boolean, CurID?: number | string, rsvRoot?: any): Promise<void> {
    if (rsvRoot !== undefined) bQuick = false;

    const desiredId = bQuick ? 0 : (typeof CurID === 'number' ? CurID : parseInt(String(CurID || 0)));
    const savedGame = rsvRoot !== undefined ? {} : await SavedGamesModule.getSave(desiredId);

    gameController.reset();
    (Globals as any).reset?.();
    GameHistory.reset();
    Globals.movingDirection = "";
    (window as any).GameCommands.runners = 0;

    TheGame = (window as any).SetupGameDataWithMap?.() || TheGame;
    GameUI.setGameTitle();
    updatePortraitLayout();
    updateInventoryLayout();
    if (!isGameCompatible(TheGame)) {
        return;
    }

    if (rsvRoot !== undefined) {
        SavedGamesModule.applyRsvToGame(TheGame!, rsvRoot);
    } else {
        if (!savedGame) {
            (window as any).showAlert?.("Load Error", "Save game not found or could not be loaded.");
            return;
        }
        await SavedGamesModule.applySaveToGame(TheGame!, savedGame as SaveData);
    }

    // Deactivate any timers that have exceeded their length but are still marked active
    GameTimers.deactivateExpiredTimers();

    // For saves created by older regalia versions that did not correctly
    // persist Active=false for fire-once timers, statically detect such
    // timers and heal them so they don't refire on every load.
    {
        const savedVersion = (savedGame as any)?.regaliaVersion;
        const isOldSave = rsvRoot !== undefined
            || isLegacyRegaliaVersion(savedVersion)
            || compareSemver(String(savedVersion || ''), '2.3.2') < 0;
        if (isOldSave) {
            GameTimers.healSuspiciousFireOnceTimers();
        }
    }

    await runGlobalScriptsOnce();
    if (savedGame && isSaveVersionMismatch(savedGame)) {
        await runEventScripts('<<On Game Version Mismatch>>');
    }

    const loadedSave = savedGame as SaveData | null;
    if (loadedSave && loadedSave.cheatFreezes) {
        (window as any).cheatFreezes = loadedSave.cheatFreezes;
    }
    RoomChange(false, false, true);
    if (loadedSave && loadedSave.currentImage !== undefined) {
        showImage(loadedSave.currentImage);
    }
    UpdateStatusBars();
    SetPortrait(TheGame!.Player.PlayerPortrait);
    $dom("#playernamechoice").css("visibility", "hidden");
    $dom("#textactionchoice").css("visibility", "hidden");
    $dom("#textchoice").css("visibility", "hidden");
    $dom("#inputmenu").css("visibility", "hidden");
    $dom("#selectionmenu").css("visibility", "hidden");
    $dom("#genderchoice").css("visibility", "hidden");
    $dom("#cmdinputmenu").css("visibility", "hidden");
    const continueBtn = $dom("#Continue").get(0) as HTMLElement;
    if (continueBtn) {
        continueBtn.setAttribute('disabled', 'disabled');
    }
    const backBtn = $dom("#back").get(0) as HTMLButtonElement;
    if (backBtn) {
        backBtn.disabled = true;
        backBtn.title = GameHistory.noGoBackReason();
    }
    GameUI.showGameElements();

    if (TheGame!.bgMusic !== undefined && TheGame!.bgMusic.length > 0) {
        GameUI.playBgMusic(imagePath(TheGame!.bgMusic));
    } else {
        GameUI.playBgMusic(null);
    }

    await runEventScripts('<<On Game Load>>');

    GameHistory.pushState();

    if (bQuick) {
        GameUI.showMessage('Quick Loaded', { type: 'success', timeout: 3.0 });
    } else {
        GameUI.showMessage('Game Loaded', { type: 'success', timeout: 3.0 });
    }
    Globals.hasUnsavedProgress = false;
    Globals.commandQueueEmptiedSinceCheckpoint = true;
};

(window as any).handleDestroySave = function (saveId: number): void {
    (window as any).showConfirm?.('Destroy Save', 'Are you sure you want to destroy this save? This action cannot be undone.').then((confirmed: boolean) => {
        if (!confirmed) {
            return;
        }
        SavedGamesModule.destroySave(saveId).then(() => {
            hideSaveAndLoadMenus();
        }).catch((err) => {
            console.error('Failed to destroy save:', err);
        });
    });
};

(window as any).handleDestroyAllSaves = function (): void {
    (window as any).showConfirm?.('Destroy All Saves', 'Are you sure you want to destroy all saves? This action cannot be undone.').then((confirmed: boolean) => {
        if (!confirmed) {
            return;
        }
        SavedGamesModule.reset().then(() => {
            hideSaveAndLoadMenus();
        }).catch((err) => {
            console.error('Failed to reset saves:', err);
        });
    });
};

(window as any).retrieveExportData = async function (): Promise<string> {
    const resultArray: any[] = [];
    const savedGames = await SavedGamesModule.getSortedSaves();
    for (const savedGame of savedGames) {
        const save = await SavedGamesModule.getSave(savedGame.id);
        if (save) {
            resultArray.push(save);
        }
    }
    return JSON.stringify(resultArray);
};

(window as any).SetupGameDataWithMap = function (): any {
    const gameData = (window as any).SetupGameData?.() as any;
    if (gameData.TurnCount === undefined) gameData.TurnCount = 0;
    Finder.addMaps(gameData);
    return gameData;
};

(window as any).receivedText = function (): void {
    try {
        // Ensure OriginalGame is initialized
        if (typeof (window as any).OriginalGame === 'undefined') {
            (window as any).OriginalGame = null;
        }

        loadGamePackage().then();
        OriginalGame = (window as any).SetupGameDataWithMap?.();
        TheGame = (window as any).SetupGameDataWithMap?.();
        GameUI.setGameTitle();
        GameUI.setDefaultCompass();
        updatePortraitLayout();
        updateInventoryLayout();
        if (!isGameCompatible(TheGame)) {
            return;
        }
        if (TheGame && TheGame.Player.bPromptForName) {
            gameController.startAwaitingInput();
            $dom("#playernamechoice").css("visibility", "visible");
            GameUI.hideGameElements();
            // Auto-focus the input field
            setTimeout(() => {
                const input = $dom("#playernametext").get(0) as HTMLInputElement;
                if (input) input.focus();
            }, 0);
            return;
        }
        if (TheGame && TheGame.Player.bPromptForGender) {
            gameController.startAwaitingInput();
            $dom("#genderchoice").css("visibility", "visible");
            GameUI.hideGameElements();
            // Setup gender selection with keyboard navigation
            setTimeout(() => {
                setupGenderSelection();
            }, 0);
            return;
        }
        (window as any).StartGame?.();
    } catch (err: any) {
        // Enhanced error logging
        const errorMessage = err?.message || String(err);
        const errorStack = err?.stack || 'No stack trace available';
        const errorName = err?.name || 'Error';
        
        console.error('Error in receivedText:', {
            name: errorName,
            message: errorMessage,
            stack: errorStack,
            error: err
        });
        
        // Log additional context
        console.error('Context:', {
            hasSetupGameDataWithMap: typeof (window as any).SetupGameDataWithMap === 'function',
            hasSetupGameData: typeof (window as any).SetupGameData === 'function',
            hasTheGame: typeof TheGame !== 'undefined',
            TheGameValue: TheGame,
            hasPlayer: typeof (window as any).player === 'function',
            hasPlayerClass: typeof (window as any).Player === 'function'
        });
        
        (window as any).showAlert?.("Error", `An error has occurred: ${errorMessage}\n\nCheck the console for more details.`);
    }
};

(window as any).StartGame = function (): void {
    if (!TheGame) return;
    Globals.hasUnsavedProgress = false;
    Globals.commandQueueEmptiedSinceCheckpoint = false;
    const currentroom = Finder.room(TheGame.Player.StartingRoom);

    const startSequence = async (): Promise<void> => {
        await runGlobalScriptsOnce();
        SetupStatusBars();
        GameUI.refreshInventory();
        AddTextToRTF(TheGame.OpeningMessage + '<br><br>', "Black", "Regular");
        SetPortrait(TheGame.Player.PlayerPortrait);
        if (currentroom != null) {
            // Await the starting room's enter events so they don't interleave
            // with <<On Game Start>> (they share the Globals stack; the first
            // chain to finish would pop the other's frames, e.g. losing a
            // pending CT_SETVARIABLEBYINPUT and freezing the name prompt).
            await ChangeRoomAsync(currentroom, true, true);
        }
        await runEventScripts("<<On Game Start>>");
        await GameActions.runEventsAsync("<<On Game Start>>");
        setTimeout(() => {
            if (gameController.shouldRunCommands()) {
                Globals.commandQueueEmptiedSinceCheckpoint = true;
            }
        }, 0);
        GameHistory.pushState();
    };

    void startSequence();
};
